/**
 * Availability dates are calendar dates, not instants in time. GraphQL returns
 * PostgreSQL DATE values as UTC-midnight timestamps, which must not be shifted
 * into the browser's timezone before they are displayed or compared.
 */
export const toCalendarDateKey = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  if (typeof value === 'string') {
    const datePrefix = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (datePrefix) return datePrefix[1];

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && value.trim() !== '') {
      return toUtcCalendarDateKey(new Date(numericValue));
    }

    const parsed = new Date(value);
    return toUtcCalendarDateKey(parsed);
  }

  if (typeof value === 'number') {
    return toUtcCalendarDateKey(new Date(value));
  }

  return null;
};

const toUtcCalendarDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toLocalCalendarDate = (value) => {
  const key = toCalendarDateKey(value);
  if (!key) return null;
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const isCalendarDateInRange = (date, startDate, endDate) => {
  const dateKey = toCalendarDateKey(date);
  const startKey = toCalendarDateKey(startDate);
  const endKey = toCalendarDateKey(endDate);
  return Boolean(dateKey && startKey && endKey && dateKey >= startKey && dateKey <= endKey);
};

export const compareCalendarDates = (left, right) => {
  const leftKey = toCalendarDateKey(left) || '';
  const rightKey = toCalendarDateKey(right) || '';
  return leftKey.localeCompare(rightKey);
};

export const isApprovedVacation = (vacation) =>
  vacation?.approvalStatus === 'APPROVED';

export const getVacationDeleteConfirmation = (vacation) =>
  isApprovedVacation(vacation)
    ? 'This time off has already been approved. Are you sure you want to permanently remove it from your availability?'
    : 'Remove this time-off request?';
